// src/auth/AuthContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import api from './api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    // Hydrate auth state on app load
    useEffect(() => {
        const token = localStorage.getItem('token');
        const savedUser = localStorage.getItem('user');

        if (token && savedUser) {
            try {
                setUser(JSON.parse(savedUser));
                // Verify token is still valid
                api.get('/auth/me')
                    .then(res => {
                        const userData = { ...res.data, token };
                        setUser(userData);
                        localStorage.setItem('user', JSON.stringify(userData));
                    })
                    .catch(() => {
                        // Token expired/invalid
                        localStorage.removeItem('token');
                        localStorage.removeItem('user');
                        setUser(null);
                    })
                    .finally(() => setIsLoading(false));
            } catch {
                localStorage.removeItem('token');
                localStorage.removeItem('user');
                setUser(null);
                setIsLoading(false);
            }
        } else {
            setIsLoading(false);
        }
    }, []);

    const login = async (username, password) => {
        const response = await api.post('/auth/login', { username, password });
        const { access_token, role, username: uname, organisation_id, organisation_name } = response.data;

        localStorage.setItem('token', access_token);
        const userData = { username: uname, role, token: access_token, organisation_id, organisation_name };
        localStorage.setItem('user', JSON.stringify(userData));
        setUser(userData);

        return userData;
    };

    const register = async (username, password, email, registrationCode) => {
        const response = await api.post('/auth/register', {
            username,
            password,
            email: email || null,
            role: 'therapist',
            registration_code: registrationCode
        });
        const { access_token, role, username: uname, organisation_id, organisation_name } = response.data;

        localStorage.setItem('token', access_token);
        const userData = { username: uname, role, token: access_token, organisation_id, organisation_name };
        localStorage.setItem('user', JSON.stringify(userData));
        setUser(userData);

        return userData;
    };

    const logout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
    };

    const value = {
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        register,
        logout,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}

export default AuthContext;
